import { LexicalEditor, $getSelection, $isRangeSelection, $isTextNode, $getNodeByKey } from 'lexical'
import { createStoreContext } from 'reusable/mobx-store-utils/storeUtils'
import DisposableReactionsStore from 'reusable/mobx-store-utils/DisposableReactionsStore'
import MarkdownStore from '../../MarkdownStore'
import { PopoverPosition } from '../../popover/PopoverSurface'
import { ContentBlockType } from '../../contentBlocks'
import { insertContentBlock } from '../../contentBlockInsertion'
import { makeAutoObservable } from 'mobx'

interface OpenState {
   anchor: PopoverPosition
   query: string
   /** Lexical text node containing the trigger `/` and any query characters. */
   nodeKey: string
   /** Offset of `/` within the text node. */
   triggerOffset: number
}

interface SlashCommandStoreProps {
   editor: LexicalEditor
   markdownStore: MarkdownStore
   /** Published via `usePublisher(insertMarkdown$)` — the in-editor way to insert
    *  markdown without needing access to the MDXEditor methods ref. */
   insertMarkdown: (markdown: string) => void
}

function getCaretRect(): PopoverPosition | null {
   const windowSelection = window.getSelection()
   if (!windowSelection || windowSelection.rangeCount === 0) return null
   const caretRect = windowSelection.getRangeAt(0).getBoundingClientRect()
   if (!caretRect) return null
   if (caretRect.width === 0 && caretRect.height === 0 && caretRect.top === 0 && caretRect.left === 0) return null
   return { left: caretRect.left, top: caretRect.top, bottom: caretRect.bottom }
}

export default class SlashCommandStore {
   sp
   open: OpenState | null = null
   /** Fingerprint of the last `/` we processed — keyed so dismissing the menu
    *  doesn't immediately re-open it on the same trigger character. */
   private consumedTrigger = ''

   constructor(sp: SlashCommandStoreProps) {
      makeAutoObservable(this)
      this.sp = sp
   }

   /** Wired up by `useStore` so the lexical update listener is disposed when the
    *  host component unmounts. */
   configureReactions = (reactionsStore: DisposableReactionsStore) => {
      reactionsStore.registerDisposer(
         this.sp.editor.registerUpdateListener(({ editorState }) => {
            editorState.read(() => this.handleEditorUpdate())
         })
      )
   }

   close = () => {
      this.open = null
   }

   handleItemSelect = (type: ContentBlockType) => {
      if (!this.open) return
      this.clearTrigger(this.open)
      const { openImageUploadRef, openMindsetUploadRef, openFileUploadRef } = this.sp.markdownStore.sp
      insertContentBlock(type, {
         insertMarkdown: this.sp.insertMarkdown,
         openImageUploadRef,
         openMindsetUploadRef,
         openFileUploadRef,
      })
      this.close()
   }

   private handleEditorUpdate = () => {
      const selection = $getSelection()
      if (!$isRangeSelection(selection) || !selection.isCollapsed()) {
         if (this.open) this.open = null
         return
      }
      const textNode = selection.anchor.getNode()
      if (!$isTextNode(textNode)) {
         if (this.open) this.open = null
         return
      }

      const text = textNode.getTextContent()
      const cursorOffset = selection.anchor.offset
      const nodeKey = textNode.getKey()

      // If the popover is already open, track query / dismissal.
      if (this.open) {
         const openState = this.open
         if (openState.nodeKey !== nodeKey) { this.open = null; return }
         if (text[openState.triggerOffset] !== '/') { this.open = null; return }
         if (cursorOffset <= openState.triggerOffset) { this.open = null; return }
         const query = text.slice(openState.triggerOffset + 1, cursorOffset)
         if (/\s/.test(query)) { this.open = null; return }
         if (query !== openState.query) this.open = { ...openState, query }
         return
      }

      // Closed: open only when caret sits right after a `/` AND that `/` is the
      // single character in its parent block (the design wants `/` on an empty line).
      if (cursorOffset < 1 || text[cursorOffset - 1] !== '/') return
      const parentBlock = textNode.getTopLevelElement()
      if (!parentBlock) return
      if (parentBlock.getTextContent() !== '/') return

      const fingerprint = `${nodeKey}:${cursorOffset}:${text.length}`
      if (this.consumedTrigger === fingerprint) return

      requestAnimationFrame(() => {
         const anchor = getCaretRect()
         if (!anchor) return
         this.consumedTrigger = fingerprint
         this.open = { anchor, query: '', nodeKey, triggerOffset: cursorOffset - 1 }
      })
   }

   /** Remove `/query` from the trigger node so the inserted markdown doesn't land
    *  alongside the literal slash. */
   private clearTrigger = (openState: OpenState) => {
      this.sp.editor.update(() => {
         const triggerNode = $getNodeByKey(openState.nodeKey)
         if (!triggerNode || !$isTextNode(triggerNode)) return
         const focusOffset = Math.min(openState.triggerOffset + 1 + openState.query.length, triggerNode.getTextContent().length)
         triggerNode.select(openState.triggerOffset, focusOffset).removeText()
      })
   }
}

export const { Provider: SlashCommandStoreProvider, useProvidedStore: useSlashCommandStore } = createStoreContext(SlashCommandStore)
